"use client"

import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { ChevronDown } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { useRouter } from "next/navigation"
import { handleSwissCTAClick, handleDefaultCTAClick } from "../utils/cta"
import { trackEvent } from "../lib/analytics"
import GeneralRosetVsGpt from "./GeneralRosetVsGpt"
import RosetVsGpt from "./RosetVsGpt"

const FaqSection = ({ isSwiss = false }) => {
  const { t } = useTranslation("FAQ")
  const faqs = t("questions", { returnObjects: true })
  const [openIndex, setOpenIndex] = useState(null)
  const [mounted, setMounted] = useState(false)
  const router = useRouter()

  useEffect(() => {
    setMounted(true)
  }, [])

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  const handleCTAClick = () => {
    trackEvent("cta_click", { location: "faq" })
    if (isSwiss) {
      handleSwissCTAClick(router)
    } else {
      handleDefaultCTAClick(router)
    }
  }

  const isComparisonQuestion = (index) => {
    return faqs[index] && faqs[index].question === t("comparisonQuestion")
  }

  if (!mounted) {
    return (
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="h-64 animate-pulse bg-white rounded-xl" />
        </div>
      </section>
    )
  }

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="text-center mb-12"
        >
          <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2">
            {t("subtitle")}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
            {t("title")}
          </h2>
        </motion.div>

        {/* FAQ List */}
        <div className="space-y-3">
          {Array.isArray(faqs) && faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-20px" }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="bg-white rounded-lg border border-slate-200 overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex justify-between items-center p-5 text-left hover:bg-slate-50/50 transition-colors duration-150"
              >
                <span className="text-sm font-medium text-slate-900 pr-4">
                  {faq.question}
                </span>
                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                </motion.div>
              </button>
              <AnimatePresence initial={false}>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5">
                      {isComparisonQuestion(index) ? (
                        <div className="bg-slate-50 rounded-lg -mx-1 p-0">
                          {isSwiss ? <RosetVsGpt /> : <GeneralRosetVsGpt />}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <button
            onClick={handleCTAClick}
            className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-3 rounded-full transition duration-200 hover:shadow-lg hover:shadow-blue-600/25"
          >
            {t(isSwiss ? "ctaButtonSwiss" : "ctaButton")}
          </button>
        </div>
      </div>
    </section>
  )
}

export default FaqSection
